import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { showGlobalNotification } from '../hooks/useNotifications';
import { apiService } from '../services/api';
import { 
  Plus, Search, Edit2, Trash2, Archive, MapPin, Building2, 
  Calendar, Camera, Eye, History, Wrench, CheckCircle, Printer, Copy, X
} from 'lucide-react';
import Modal from './Modal';

interface Cabinet {
  id: number;
  cabinet_name: string;
  location_id: number;
  company_id: number;
  description: string;
  image_path: string;
  last_cleaning_date: string;
  location_name: string;
  company_name: string;
  switch_count: number;
  switches?: any[];
  history?: any[];
  created_at: string;
  updated_at: string;
}

const CabinetsPage: React.FC = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showCleaningModal, setShowCleaningModal] = useState(false);
  const [showLabelModal, setShowLabelModal] = useState(false);
  const [labelSettings, setLabelSettings] = useState({
    width: 100,
    height: 50,
    orientation: 'horizontal'
  });
  const [editingCabinet, setEditingCabinet] = useState<Cabinet | null>(null);
  const [selectedCabinet, setSelectedCabinet] = useState<Cabinet | null>(null);
  const [cabinetsData, setCabinetsData] = useState<Cabinet[]>([]);
  const [companiesData, setCompaniesData] = useState<any[]>([]);
  const [locationsData, setLocationsData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [imageFile, setImageFile] = useState<File | null>(null);
  
  const [cabinetData, setCabinetData] = useState({
    cabinet_name: '',
    location_id: 0,
    company_id: 0,
    description: '',
    last_cleaning_date: ''
  });

  const [cleaningData, setCleaningData] = useState({
    cleaning_date: new Date().toISOString().split('T')[0],
    notes: ''
  });

  React.useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [cabinetsResponse, companiesResponse, locationsResponse] = await Promise.all([
        apiService.getCabinets(),
        apiService.getCompanies(),
        apiService.getLocations()
      ]);
      setCabinetsData(Array.isArray(cabinetsResponse) ? cabinetsResponse : []);
      setCompaniesData(Array.isArray(companiesResponse) ? companiesResponse : []);
      setLocationsData(Array.isArray(locationsResponse) ? locationsResponse : []);
    } catch (error) {
      console.error('Error loading data:', error);
      showGlobalNotification.error(t('cabinet.errTitle'), t('cabinet.errLoadData'));
    } finally {
      setLoading(false);
    }
  };

  const filteredCabinets = cabinetsData.filter(cabinet =>
    String(cabinet.cabinet_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(cabinet.location_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(cabinet.company_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(cabinet.description || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleEdit = (cabinet: Cabinet) => {
    // Admin, superadmin ve teknisyen düzenleyebilir
    if (!['admin', 'superadmin', 'teknisyen'].includes(user?.role)) {
      showGlobalNotification.error(t('cabinet.errTitle'), t('cabinet.errNoPermission'));
      return;
    }

    setEditingCabinet(cabinet);
    setCabinetData({
      cabinet_name: cabinet.cabinet_name,
      location_id: cabinet.location_id,
      company_id: cabinet.company_id,
      description: cabinet.description || '',
      last_cleaning_date: cabinet.last_cleaning_date ? cabinet.last_cleaning_date.split('T')[0] : ''
    });
    setImageFile(null);
    setShowModal(true);
  };

  const handleAdd = () => {
    // Admin, superadmin ve teknisyen ekleyebilir
    if (!['admin', 'superadmin', 'teknisyen'].includes(user?.role)) {
      showGlobalNotification.error(t('cabinet.errTitle'), t('cabinet.errNoPermission'));
      return;
    }

    setEditingCabinet(null);
    setCabinetData({
      cabinet_name: '',
      location_id: 0,
      company_id: 0,
      description: '',
      last_cleaning_date: ''
    });
    setImageFile(null);
    setShowModal(true);
  };

  const handleCopy = (cabinet: Cabinet) => {
    // Admin, superadmin ve teknisyen kopyalayabilir
    if (!['admin', 'superadmin', 'teknisyen'].includes(user?.role)) {
      showGlobalNotification.error(t('cabinet.errTitle'), t('cabinet.errNoPermission'));
      return;
    }

    setEditingCabinet(null);
    setCabinetData({
      cabinet_name: `${cabinet.cabinet_name} - Kopya`,
      location_id: cabinet.location_id,
      company_id: cabinet.company_id,
      description: cabinet.description || '',
      last_cleaning_date: ''
    });
    setImageFile(null);
    setShowModal(true);
    showGlobalNotification.info(t('cabinet.infoTitle'), t('cabinet.infoCopied'));
  };

  const handleSave = async () => {
    if (!cabinetData.cabinet_name) {
      showGlobalNotification.error(t('cabinet.errTitle'), t('cabinet.errNameRequired'));
      return;
    }
    
    try {
      const formData = new FormData();
      Object.keys(cabinetData).forEach(key => {
        formData.append(key, cabinetData[key as keyof typeof cabinetData].toString());
      });
      
      if (imageFile) {
        formData.append('image', imageFile);
      }

      if (editingCabinet) {
        await apiService.updateCabinet(editingCabinet.id, formData);
        showGlobalNotification.success(t('cabinet.successUpdatedTitle'), `${cabinetData.cabinet_name} ${t('cabinet.successUpdatedMsg')}`);
        
        // Header notification için API çağrısı
        await apiService.logActivity({
          action: 'cabinet_update',
          description: `${cabinetData.cabinet_name} kabini güncellendi`,
          category: 'cabinet'
        });
      } else {
        await apiService.createCabinet(formData);
        showGlobalNotification.success(t('cabinet.successCreatedTitle'), `${cabinetData.cabinet_name} ${t('cabinet.successCreatedMsg')}`);
        
        // Header notification için API çağrısı
        await apiService.logActivity({
          action: 'cabinet_create',
          description: `${cabinetData.cabinet_name} kabini oluşturuldu`,
          category: 'cabinet'
        });
      }
      
      await loadData();
      setShowModal(false);
    } catch (error: any) {
      console.error('Error saving cabinet:', error);
      showGlobalNotification.error(t('cabinet.errTitle'), error.message || t('cabinet.errSave'));
    }
  };

  const handleDelete = async (id: number) => {
    // Sadece admin, superadmin ve teknisyen silebilir
    if (!['admin', 'superadmin', 'teknisyen'].includes(user?.role)) {
      showGlobalNotification.error(t('cabinet.errTitle'), t('cabinet.errNoPermission'));
      return;
    }

    const cabinet = cabinetsData.find(c => c.id === id);
    const cabinetName = cabinet?.cabinet_name || t('cabinet.unknownCabinet');

    if (confirm(t('cabinet.confirmDelete'))) {
      try {
        await apiService.deleteCabinet(id);
        await loadData();
        showGlobalNotification.success(t('cabinet.successDeletedTitle'), `${cabinetName} ${t('cabinet.successDeletedMsg')}`);
        
        // Header notification için API çağrısı
        await apiService.logActivity({
          action: 'cabinet_delete',
          description: `${cabinetName} kabini silindi`,
          category: 'cabinet'
        });
      } catch (error: any) {
        console.error('Error deleting cabinet:', error);
        showGlobalNotification.error(t('cabinet.errTitle'), error.message || t('cabinet.errDelete'));
      }
    }
  };

  const showCabinetDetail = async (cabinet: Cabinet) => {
    try {
      const detailedCabinet = await apiService.getCabinet(cabinet.id);
      setSelectedCabinet(detailedCabinet);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Error fetching cabinet details:', error);
      showGlobalNotification.error(t('cabinet.errTitle'), t('cabinet.errLoadDetail'));
    }
  };

  const handleCleaning = (cabinet: Cabinet) => {
    setSelectedCabinet(cabinet);
    setCleaningData({
      cleaning_date: new Date().toISOString().split('T')[0],
      notes: ''
    });
    setShowCleaningModal(true);
  };

  const saveCleaning = async () => {
    if (!selectedCabinet) return;
    
    try {
      await apiService.addCabinetCleaning(selectedCabinet.id, cleaningData);
      showGlobalNotification.success(t('cabinet.successCleaningTitle'), `${selectedCabinet.cabinet_name} ${t('cabinet.successCleaningMsg')}`);
      
      // Header notification için API çağrısı
      await apiService.logActivity({
        action: 'cabinet_cleaning',
        description: `${selectedCabinet.cabinet_name} kabini temizlendi`,
        category: 'cabinet'
      });
      
      await loadData();
      setShowCleaningModal(false);
    } catch (error: any) {
      console.error('Error saving cleaning:', error);
      showGlobalNotification.error(t('cabinet.errTitle'), error.message || t('cabinet.errCleaning'));
    }
  };

  const handleLabelPrint = (cabinet: Cabinet) => {
    setSelectedCabinet(cabinet);
    setShowLabelModal(true);
  };

  const printCabinetLabel = async (includeSwitches: boolean) => {
    if (!selectedCabinet) return;
    
    try {
      const detailedCabinet = await apiService.getCabinet(selectedCabinet.id);
      
      const printWindow = window.open('', '_blank');
      if (!printWindow) return;
      
      const isVertical = labelSettings.orientation === 'vertical';
      const width = isVertical ? labelSettings.height : labelSettings.width;
      const height = isVertical ? labelSettings.width : labelSettings.height;
      
      const labelHTML = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Kabin Etiketi - ${detailedCabinet.cabinet_name}</title>
          <style>
            @page {
              size: ${width}mm ${height}mm;
              margin: 0;
            }
            @media print {
              html, body {
                width: ${width}mm;
                height: ${height}mm;
                margin: 0;
                padding: 0;
              }
            }
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: 'Arial', sans-serif; 
              background: white;
              width: ${width}mm !important;
              height: ${height}mm !important;
              padding: ${labelSettings.margin_top}mm ${labelSettings.margin_right}mm ${labelSettings.margin_bottom}mm ${labelSettings.margin_left}mm;
              display: flex;
              justify-content: center;
              align-items: center;
            }
            .label {
              width: ${width - 4}mm;
              height: ${height - 4}mm;
              border: 2px solid #2563eb;
              border-radius: 4px;
              padding: 3mm;
              background: white;
              display: flex;
              flex-direction: column;
              justify-content: space-between;
            }
            .header {
              text-align: center;
              border-bottom: 1px solid #2563eb;
              padding-bottom: 2mm;
            }
            .cabinet-name {
              font-size: 20px;
              font-weight: bold;
              color: #1e40af;
              margin-bottom: 1mm;
              word-wrap: break-word;
            }
            .location {
              font-size: 12px;
              color: #64748b;
              font-weight: bold;
              word-wrap: break-word;
            }
            .content {
              flex-grow: 1;
              display: flex;
              flex-direction: column;
              justify-content: center;
              margin: 2mm 0;
            }
            .switches-section {
              text-align: center;
            }
            .section-title {
              font-size: 12px;
              font-weight: bold;
              color: #374151;
              margin-bottom: 2mm;
              text-transform: uppercase;
            }
            .switch-item {
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding: 1mm 2mm;
              margin-bottom: 1mm;
              background: #f8fafc;
              border-radius: 2mm;
              border-left: 2px solid #10b981;
              font-size: 8px;
            }
            .switch-info {
              display: flex;
              flex-direction: column;
              text-align: left;
            }
            .switch-name {
              font-weight: bold;
              color: #111827;
              font-size: 11px;
            }
            .switch-details {
              font-size: 9px;
              font-weight: bold;
              color: #6b7280;
              margin-top: 0.5mm;
            }
            .switch-ip {
              color: #1e40af;
              font-family: 'Courier New', monospace;
              font-weight: bold;
              background: #dbeafe;
              padding: 0.5mm 1mm;
              border-radius: 1mm;
              font-size: 10px;
            }
            .no-switches {
              text-align: center;
              color: #000000;
              font-size: 16px;
              font-weight: bold;
              padding: 2mm;
              background: #f9fafb;
              border-radius: 2mm;
              border: 1px dashed #d1d5db;
            }
            .footer {
              text-align: center;
              font-size: 14px;
              font-weight: bold;
              color: #000000;
              border-top: 1px solid #d1d5db;
              padding-top: 1mm;
            }
            .cabinet-icon {
              width: 3mm;
              height: 3mm;
              background: #2563eb;
              border-radius: 1mm;
              display: inline-block;
              margin-right: 1mm;
            }
            @media print {
              body { 
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
            }
          </style>
        </head>
        <body>
          <div class="label">
            <div class="header">
              <div class="cabinet-name">
                <span class="cabinet-icon"></span>
                ${detailedCabinet.cabinet_name}
              </div>
              <div class="location">
                <strong>${detailedCabinet.company_name || 'Şirket Belirtilmemiş'}</strong> • <strong>${detailedCabinet.location_name || 'Lokasyon Belirtilmemiş'}</strong>
              </div>
            </div>
            
            <div class="content">
              ${includeSwitches ? `
                <div class="switches-section">
                  <div class="section-title">Network Switchleri</div>
                  ${detailedCabinet.switches && detailedCabinet.switches.length > 0 
                    ? detailedCabinet.switches.slice(0, 3).map((sw: any) => `
                      <div class="switch-item">
                        <div class="switch-info">
                          <span class="switch-name">${sw.switch_name}</span>
                          <span class="switch-details">${sw.brand} ${sw.model}</span>
                        </div>
                        <span class="switch-ip">${sw.ip_address}</span>
                      </div>
                    `).join('')
                    : '<div class="no-switches">Bu kabinde switch bulunmuyor</div>'
                  }
                  ${detailedCabinet.switches && detailedCabinet.switches.length > 3 ? `
                    <div style="font-size: 8px; color: #6b7280; margin-top: 1mm;">+${detailedCabinet.switches.length - 3} switch daha...</div>
                  ` : ''}
                </div>
              ` : `
                <div class="no-switches">Kabin Etiketi</div>
              `}
            </div>
            
            <div class="footer">
              <span class="cabinet-icon"></span>
              IT Toolbox - ${new Date().toLocaleDateString('tr-TR')}
            </div>
          </div>
        </body>
        </html>
      `;
      
      printWindow.document.write(labelHTML);
      printWindow.document.close();
      
      setTimeout(() => {
        printWindow.print();
      }, 500);
      
      setShowLabelModal(false);
      
    } catch (error) {
      console.error('Error printing label:', error);
      showGlobalNotification.error(t('cabinet.errTitle'), t('cabinet.errPrint'));
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">{t('cabinet.title')}</h1>
          <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base">{t('cabinet.subtitle')}</p>
        </div>
        {['admin', 'superadmin', 'teknisyen'].includes(user?.role) && (
          <button
            onClick={handleAdd}
            className="flex items-center justify-center px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors min-h-[44px] text-sm sm:text-base w-full sm:w-auto"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('cabinet.newCabinet')}
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{t('cabinet.statTotal')}</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-gray-100">{cabinetsData.length}</p>
            </div>
            <Archive className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{t('cabinet.statSwitches')}</p>
              <p className="text-lg sm:text-2xl font-bold text-green-600">
                {cabinetsData.reduce((sum, c) => sum + c.switch_count, 0)}
              </p>
            </div>
            <Wrench className="w-6 h-6 sm:w-8 sm:h-8 text-green-600" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{t('cabinet.statCleanedThisMonth')}</p>
              <p className="text-lg sm:text-2xl font-bold text-purple-600">
                {cabinetsData.filter(c => {
                  if (!c.last_cleaning_date) return false;
                  const cleaningDate = new Date(c.last_cleaning_date);
                  const now = new Date();
                  return cleaningDate.getMonth() === now.getMonth() && 
                         cleaningDate.getFullYear() === now.getFullYear();
                }).length}
              </p>
            </div>
            <CheckCircle className="w-6 h-6 sm:w-8 sm:h-8 text-purple-600" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{t('cabinet.statNeedsCleaning')}</p>
              <p className="text-lg sm:text-2xl font-bold text-orange-600">
                {cabinetsData.filter(c => {
                  if (!c.last_cleaning_date) return true;
                  const cleaningDate = new Date(c.last_cleaning_date);
                  const thirtyDaysAgo = new Date();
                  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
                  return cleaningDate < thirtyDaysAgo;
                }).length}
              </p>
            </div>
            <Calendar className="w-6 h-6 sm:w-8 sm:h-8 text-orange-600" />
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder={t('cabinet.search')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent w-full max-w-md"
          />
        </div>
      </div>

      {/* Cabinets Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('cabinet.colName')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('cabinet.colCompany')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('cabinet.colLocation')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('cabinet.colSwitchCount')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('cabinet.colDescription')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('cabinet.colLastCleaning')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('cabinet.colActions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredCabinets.map((cabinet) => (
                <tr key={cabinet.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Archive className="w-5 h-5 text-primary mr-2 flex-shrink-0" />
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {cabinet.cabinet_name}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                      <Building2 className="w-4 h-4 mr-2 flex-shrink-0 text-gray-400" />
                      {cabinet.company_name || t('cabinet.notSpecified')}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                      <MapPin className="w-4 h-4 mr-2 flex-shrink-0 text-gray-400" />
                      {cabinet.location_name || t('cabinet.notSpecified')}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="relative group">
                      <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                        <Wrench className="w-4 h-4 mr-2 flex-shrink-0 text-gray-400" />
                        <span className={cabinet.switch_count > 0 ? 'cursor-help' : ''}>
                          {cabinet.switch_count}
                        </span>
                      </div>
                      {cabinet.switch_count > 0 && cabinet.switches && (
                        <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block z-10 bg-gray-900 text-white text-xs rounded-lg p-3 shadow-lg min-w-64">
                          <div className="font-medium mb-2 text-center border-b border-gray-700 pb-1">{t('cabinet.switchesInCabinet')}</div>
                          {cabinet.switches.slice(0, 4).map((sw: any, index: number) => (
                            <div key={index} className="flex flex-col py-1 border-b border-gray-700 last:border-b-0">
                              <div className="flex justify-between items-center">
                                <span className="font-medium text-accent truncate mr-2">{sw.switch_name}</span>
                                <span className="text-green-300 font-mono text-xs bg-gray-800 px-2 py-0.5 rounded">{sw.ip_address}</span>
                              </div>
                              <div className="text-gray-400 text-xs mt-0.5">
                                {sw.brand} {sw.model} • {sw.port_count} port
                              </div>
                            </div>
                          ))}
                          {cabinet.switches.length > 4 && (
                            <div className="text-gray-400 text-xs mt-2 text-center">
                              +{cabinet.switches.length - 4} {t('cabinet.moreSwitches')}
                            </div>
                          )}
                          <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-600 dark:text-gray-400 max-w-xs truncate" title={cabinet.description}>
                      {cabinet.description || '-'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm">
                      {cabinet.last_cleaning_date ? (
                        <div className="flex flex-col">
                          <span className="text-gray-900 dark:text-gray-100">{new Date(cabinet.last_cleaning_date).toLocaleDateString('tr-TR')}</span>
                          <button
                            onClick={() => handleCleaning(cabinet)}
                            className="mt-1 text-xs text-green-600 hover:text-green-700 font-medium"
                          >
                            {t('cabinet.newCleaning')}
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleCleaning(cabinet)}
                          className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs hover:bg-orange-200 transition-colors"
                        >
                          {t('cabinet.firstCleaning')}
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end items-center space-x-2">
                      <button
                        onClick={() => showCabinetDetail(cabinet)}
                        className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:text-gray-100"
                        title={t('cabinet.actionView')}
                      >
                        <Eye className="w-5 h-5" />
                      </button>
                      {['admin', 'superadmin', 'teknisyen'].includes(user?.role) && (
                        <>
                          <button
                            onClick={() => handleCopy(cabinet)}
                            className="text-green-600 hover:text-green-900"
                            title={t('cabinet.actionCopy')}
                          >
                            <Copy className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleEdit(cabinet)}
                            className="text-primary hover:text-primary-dark"
                            title={t('cabinet.actionEdit')}
                          >
                            <Edit2 className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleLabelPrint(cabinet)}
                            className="text-purple-600 hover:text-purple-900"
                            title={t('cabinet.actionLabel')}
                          >
                            <Printer className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDelete(cabinet.id)}
                            className="text-red-600 hover:text-red-900"
                            title={t('cabinet.actionDelete')}
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredCabinets.length === 0 && (
          <div className="text-center py-12">
            <Archive className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">{t('cabinet.notFound')}</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {searchTerm ? t('cabinet.notFoundSearch') : t('cabinet.notFoundEmpty')}
            </p>
          </div>
        )}
      </div>

      {/* Cabinet Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-2xl max-h-[85vh] overflow-y-auto">
            <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {editingCabinet ? t('cabinet.editTitle') : t('cabinet.addTitle')}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-400 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-4 sm:p-6 space-y-4 max-h-[70vh] sm:max-h-none overflow-y-auto">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('cabinet.fieldName')}
                </label>
                <input
                  type="text"
                  value={cabinetData.cabinet_name}
                  onChange={(e) => setCabinetData({...cabinetData, cabinet_name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder={t('cabinet.fieldNamePlaceholder')}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('cabinet.fieldCompany')}
                  </label>
                  <select
                    value={cabinetData.company_id}
                    onChange={(e) => setCabinetData({...cabinetData, company_id: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value={0}>{t('cabinet.selectCompany')}</option>
                    {companiesData.map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('cabinet.fieldLocation')}
                  </label>
                  <select
                    value={cabinetData.location_id}
                    onChange={(e) => setCabinetData({...cabinetData, location_id: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value={0}>{t('cabinet.selectLocation')}</option>
                    {locationsData.map((location) => (
                      <option key={location.id} value={location.id}>
                        {location.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('cabinet.fieldDescription')}
                </label>
                <textarea
                  value={cabinetData.description}
                  onChange={(e) => setCabinetData({...cabinetData, description: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder={t('cabinet.fieldDescPlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('cabinet.fieldLastCleaning')}
                </label>
                <input
                  type="date"
                  value={cabinetData.last_cleaning_date}
                  onChange={(e) => setCabinetData({...cabinetData, last_cleaning_date: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('cabinet.fieldPhoto')}
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setImageFile(e.target.files?.[0] || null)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>

            <div className="p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
              <button
                onClick={() => setShowModal(false)}
                className="w-full sm:w-auto px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700"
              >
                {t('cabinet.btnCancel')}
              </button>
              <button
                onClick={handleSave}
                className="w-full sm:w-auto px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
              >
                {editingCabinet ? t('cabinet.btnUpdate') : t('cabinet.btnSave')}
              </button>
            </div>
          </div>
        </Modal>

      {/* Detail Modal */}
      <Modal isOpen={showDetailModal} onClose={() => setShowDetailModal(false)}>
          {selectedCabinet && (
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {selectedCabinet.cabinet_name} - {t('cabinet.detailTitle')}
                </h2>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:text-gray-400"
                >
                  ✕
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Cabinet Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('cabinet.detailInfo')}</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('cabinet.detailCompany')}:</span>
                      <span className="font-medium">{selectedCabinet.company_name || t('cabinet.notSpecified')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('cabinet.detailLocation')}:</span>
                      <span className="font-medium">{selectedCabinet.location_name || t('cabinet.notSpecified')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('cabinet.detailLastCleaning')}:</span>
                      <span className="font-medium">
                        {selectedCabinet.last_cleaning_date
                          ? new Date(selectedCabinet.last_cleaning_date).toLocaleDateString('tr-TR')
                          : t('cabinet.noRecord')
                        }
                      </span>
                    </div>
                  </div>
                </div>

                {selectedCabinet.image_path && (
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('cabinet.detailPhoto')}</h3>
                    <img
                      src={`${import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:3004'}${selectedCabinet.image_path}`}
                      alt={selectedCabinet.cabinet_name}
                      className="w-full h-48 object-cover rounded-lg"
                    />
                  </div>
                )}
              </div>

              {/* Switches */}
              {selectedCabinet.switches && selectedCabinet.switches.length > 0 && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                    {t('cabinet.switchesInCabinet')} ({selectedCabinet.switches.length})
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedCabinet.switches.map((sw: any) => (
                      <div key={sw.id} className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="font-medium text-gray-900 dark:text-gray-100 text-sm">{sw.switch_name}</h4>
                          <span className="bg-primary/20 text-primary-dark text-xs font-mono px-2 py-1 rounded">
                            {sw.ip_address}
                          </span>
                        </div>
                        <div className="space-y-1">
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            <span className="font-medium">Model:</span> {sw.brand} {sw.model}
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            <span className="font-medium">Port:</span> {sw.port_count} adet
                          </p>
                          {sw.serial_number && (
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              <span className="font-medium">Seri No:</span> {sw.serial_number}
                            </p>
                          )}
                          {sw.location_name && (
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              <span className="font-medium">Lokasyon:</span> {sw.location_name}
                            </p>
                          )}
                          {sw.status && (
                            <div className="flex items-center mt-2">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                sw.status === 'active' ? 'bg-green-100 text-green-800' :
                                sw.status === 'inactive' ? 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200' :
                                sw.status === 'maintenance' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {sw.status === 'active' ? t('cabinet.statusActive') :
                                 sw.status === 'inactive' ? t('cabinet.statusInactive') :
                                 sw.status === 'maintenance' ? t('cabinet.statusMaintenance') : t('cabinet.statusFaulty')}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* History */}
              {selectedCabinet.history && selectedCabinet.history.length > 0 && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                    {t('cabinet.detailHistory')}
                  </h3>
                  <div className="space-y-3">
                    {selectedCabinet.history.map((record: any) => (
                      <div key={record.id} className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <History className="w-5 h-5 text-gray-400 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {record.description}
                          </p>
                          <div className="flex justify-between items-center mt-1">
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {record.user_name || 'Sistem'}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {new Date(record.created_at).toLocaleString('tr-TR')}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          )}
        </Modal>

      {/* Cleaning Modal */}
      <Modal isOpen={showCleaningModal} onClose={() => setShowCleaningModal(false)}>
          {selectedCabinet && (
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-md">
            <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {t('cabinet.cleaningModalTitle')} - {selectedCabinet.cabinet_name}
                </h2>
                <button
                  onClick={() => setShowCleaningModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-400 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-4 sm:p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('cabinet.cleaningDate')}
                </label>
                <input
                  type="date"
                  value={cleaningData.cleaning_date}
                  onChange={(e) => setCleaningData({...cleaningData, cleaning_date: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('cabinet.cleaningNotes')}
                </label>
                <textarea
                  value={cleaningData.notes}
                  onChange={(e) => setCleaningData({...cleaningData, notes: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder={t('cabinet.cleaningNotesPlaceholder')}
                />
              </div>
            </div>

            <div className="p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
              <button
                onClick={() => setShowCleaningModal(false)}
                className="w-full sm:w-auto px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700"
              >
                {t('cabinet.btnCancel')}
              </button>
              <button
                onClick={saveCleaning}
                className="w-full sm:w-auto px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              >
                {t('cabinet.btnSave')}
              </button>
            </div>
          </div>
          )}
        </Modal>

      {/* Label Print Modal */}
      <Modal isOpen={showLabelModal} onClose={() => setShowLabelModal(false)}>
          {selectedCabinet && (
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-md">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {t('cabinet.labelModalTitle')} - {selectedCabinet.cabinet_name}
                </h2>
                <button
                  onClick={() => setShowLabelModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-400 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <div className="text-center mb-6">
                <Printer className="w-12 h-12 text-purple-600 mx-auto mb-3" />
                <p className="text-gray-600 dark:text-gray-400 mb-2">
                  {t('cabinet.labelWillPrint')}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('cabinet.labelIncludeSwitches')}
                </p>
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('cabinet.labelOrientation')}
                </label>
                <div className="flex space-x-4 justify-center">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="horizontal"
                      checked={labelSettings.orientation === 'horizontal'}
                      onChange={(e) => setLabelSettings({...labelSettings, orientation: e.target.value})}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{t('cabinet.labelHorizontal')}</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="vertical"
                      checked={labelSettings.orientation === 'vertical'}
                      onChange={(e) => setLabelSettings({...labelSettings, orientation: e.target.value})}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{t('cabinet.labelVertical')}</span>
                  </label>
                </div>
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Etiket Boyutları
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                      Genişlik (mm)
                    </label>
                    <input
                      type="number"
                      min="50"
                      max="200"
                      value={labelSettings.width}
                      onChange={(e) => setLabelSettings({...labelSettings, width: parseInt(e.target.value) || 100})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                      Yükseklik (mm)
                    </label>
                    <input
                      type="number"
                      min="30"
                      max="100"
                      value={labelSettings.height}
                      onChange={(e) => setLabelSettings({...labelSettings, height: parseInt(e.target.value) || 50})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  <button
                    onClick={() => setLabelSettings({width: 100, height: 50})}
                    className="px-3 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600 dark:bg-gray-600 transition-colors"
                  >
                    100x50mm
                  </button>
                  <button
                    onClick={() => setLabelSettings({width: 90, height: 50})}
                    className="px-3 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600 dark:bg-gray-600 transition-colors"
                  >
                    90x50mm
                  </button>
                  <button
                    onClick={() => setLabelSettings({width: 80, height: 40})}
                    className="px-3 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600 dark:bg-gray-600 transition-colors"
                  >
                    80x40mm
                  </button>
                </div>
              </div>
              
              {selectedCabinet.switch_count > 0 && (
                <div className="bg-primary/10 border border-primary/30 rounded-lg p-4 mb-6">
                  <div className="flex items-center mb-2">
                    <Wrench className="w-4 h-4 text-primary mr-2" />
                    <span className="text-sm font-medium text-primary-dark">
                      Bu kabinde {selectedCabinet.switch_count} adet switch bulunuyor
                    </span>
                  </div>
                  <p className="text-xs text-primary-dark">
                    Switch bilgileri dahil edilirse etiket üzerinde görüntülenecektir.
                  </p>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex flex-col space-y-3">
              <button
                onClick={() => printCabinetLabel(true)}
                className="w-full px-4 py-3 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors flex items-center justify-center"
              >
                <Wrench className="w-4 h-4 mr-2" />
                Network Switchleri ile Yazdır ({labelSettings.width}x{labelSettings.height}mm)
              </button>
              <button
                onClick={() => printCabinetLabel(false)}
                className="w-full px-4 py-3 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors flex items-center justify-center"
              >
                <Archive className="w-4 h-4 mr-2" />
                Sadece Kabin Bilgisi ile Yazdır ({labelSettings.width}x{labelSettings.height}mm)
              </button>
              <button
                onClick={() => setShowLabelModal(false)}
                className="w-full px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors"
              >
                İptal
              </button>
            </div>
          </div>
          )}
        </Modal>
    </div>
  );
};

export default CabinetsPage;